import { FC, useMemo } from 'react'
import { MixedAccountsTableItem, isLiveUpdateStartedSelector } from 'selectors/linkageSelectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import SelfIncrementingProgressbarContainer from 'components/features/pfm/SelfIncrementingProgressbar/SelfIncrementingProgressBarContainer'
import { LinkedAccountBalanceState } from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalanceState'
import { LinkedAccountBalanceAmount } from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalanceAmount'
import styles from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalance.styl'
import { LinkedAccountBalanceNote } from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalanceNote'
import { isInProgressState } from 'helpers/linkageHelpers'
import { ProviderAccountState } from 'interfaces/linkage'
import { LinkedAccountBalanceUpdating } from 'components/features/pfm/LinkedAccountBalance/LinkedAccountBalanceUpdating'

type LinkedAccountBalanceProps = {
  accountInfo: MixedAccountsTableItem
}
export const LinkedAccountBalance: FC<LinkedAccountBalanceProps> = (props) => {
  const { accountInfo } = props
  const { providerAccount, account, type } = accountInfo

  if (type === 'manual' && account) {
    return (
      <LinkedAccountBalanceAmount account={ account } />
    )
  }

  if (!providerAccount) {
    return null
  }

  return (
    <LinkedAccountBalanceInner
      providerAccount={ providerAccount }
      { ...props }
    />
  )
}

type LinkedAccountBalanceInnerProps =
  & LinkedAccountBalanceProps
  & {
    accountInfo: MixedAccountsTableItem
    providerAccount: NonNullable<MixedAccountsTableItem['providerAccount']>
  }
const LinkedAccountBalanceInner: FC<LinkedAccountBalanceInnerProps> = (props) => {
  const { providerAccount, accountInfo } = props
  const { account, providerAccountState } = accountInfo
  const {
    name: accountName = null,
    link_status = null,
  } = account || {}

  const { message, status } = providerAccountState
  const isAccountInProgress = isInProgressState(providerAccountState as ProviderAccountState)
  const isUpdatingStatus = status === 'updating'

  const progressChild = useMemo(() => {
    if (!isAccountInProgress && !isUpdatingStatus) {
      return null
    }
    return (
      <SelfIncrementingProgressbarContainer
        qaType='linkage-progress'
        qaMarker={ accountName || '' }
        cid={ providerAccount.cid }
        isCompact
      />
    )
  }, [providerAccount.cid, isAccountInProgress, accountName, isUpdatingStatus])

  const hasProgressChild = !!progressChild
  const accountStateChild = useMemo(() => {
    if (!message) {
      return null
    }

    const child = (
      <LinkedAccountBalanceState accountInfo={ accountInfo } hideIcon={ hasProgressChild } />
    )

    if (hasProgressChild) {
      return child
    }

    return (
      <div className={ styles.accountState }>
        { child }
      </div>
    )
  }, [message, accountInfo, hasProgressChild])

  const isUnlinked = link_status === 'unlinked'
  const isLiveUpdateStarted = useStoreSelector(isLiveUpdateStartedSelector)

  const accountChild = useMemo(() => (
    <>
      { !!account && (
        <LinkedAccountBalanceAmount account={ account } isLiveUpdate={ isLiveUpdateStarted } />
      )}
    </>
  ), [account, isLiveUpdateStarted])


  if (isUnlinked) {
    return accountChild
  }

  if (isLiveUpdateStarted && !isAccountInProgress) {
    if (account) {
      return accountChild
    }

    return (
      <LinkedAccountBalanceNote>
        <div className={ styles.liveUpdate }>
          <LinkedAccountBalanceUpdating />
        </div>
      </LinkedAccountBalanceNote>
    )
  }

  if (progressChild || accountStateChild) {
    return (
      <div className={ styles.linkedAccountBalance }>
        { progressChild }
        { accountStateChild }
      </div>
    )
  }

  return accountChild
}
